// Yin Yoga Bath - Service + Location Page (SEO Enhanced)
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car, Check, Users, Clock, Heart } from "lucide-react";
import Navigation from "../../../components/shared/Navigation";
import Footer from "../../../components/shared/Footer";
import PageBreadcrumb from "../../../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../../components/shared/SEOHelper";

const YinYogaBath = () => {
  usePageMeta({
    title: "Yin Yoga Bath | Slow Yoga | Annelie Lily",
    description: "Yin yoga classes for Bath residents. Slow, deep stretches for release and relaxation. Based in Frome, 25 minutes from Bath. All levels welcome.",
    canonical: "https://annelielily.com/areas/yoga-bath/yin-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Yin Yoga Classes for Bath",
    description: "Yin yoga classes for Bath residents at Annelie Lily Yoga in Frome. Slow, meditative practice for deep release.",
    serviceType: "Yoga Classes",
    url: "https://annelielily.com/areas/yoga-bath/yin-yoga"
  });

  const faqs = [
    { question: "What is Yin yoga?", answer: "Yin is a slow, meditative style where we hold poses for 3-5 minutes. It targets deep connective tissue rather than muscles, releasing tension that ordinary stretching doesn't reach." },
    { question: "Is Yin yoga easier than Hatha?", answer: "It's different rather than easier. Yin is physically gentler but can be mentally challenging — sitting with stillness takes practice. It's excellent for stress relief." },
    { question: "How do I get to Frome from Bath?", answer: "Take the A36 south through Woolverton, then into Frome. About 25 minutes, mostly single carriageway. Many students enjoy the scenic drive as part of their wind-down." },
    { question: "Can I do Yin yoga if I'm very inflexible?", answer: "Absolutely. Yin is ideal for tight bodies. We use props to make poses comfortable, and the long holds give your body time to release gradually without forcing anything." },
    { question: "How often should I practice Yin?", answer: "Once a week complements more active exercise well. Some students do Yin alongside Hatha — the styles balance each other beautifully." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yin Yoga" currentSlug="areas/yoga-bath/yin-yoga" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Yin Yoga for Bath</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Yin Yoga Bath</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Slow, deep yoga for Bath residents seeking release and relaxation. 
              Long-held poses targeting connective tissue — perfect for tight hips, 
              lower back tension, or simply needing to properly slow down.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />25 min from Bath</span>
            </div>
            <Link to="/contact" className="btn-primary" data-testid="hero-cta">Book a Yin Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Snippet / Direct Answer */}
      <section className="py-10 px-6 md:px-12 bg-cream border-b border-gold/10">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl text-neutral-800 mb-3">What is Yin yoga?</h2>
          <p className="text-neutral-700">
            Yin yoga is a slow, meditative practice where poses are held for 3-5 minutes. Unlike active yoga styles, Yin targets the deep connective tissues — fascia, ligaments, and joints — releasing tension that regular stretching doesn't reach. It's deeply calming and ideal for stress relief.
          </p>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">Who is Yin yoga for?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: "Stressed & overwhelmed", desc: "The slow pace and stillness help calm an overactive nervous system" },
              { title: "Tight hips & back", desc: "Long holds target the areas where most people carry tension" },
              { title: "Active people", desc: "Complements running, cycling, or strength training perfectly" },
              { title: "Anyone needing to slow down", desc: "A counter-balance to busy modern life" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">What to expect in a Yin class</h2>
          <div className="space-y-6 text-neutral-700">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Settling in</h3>
                <p className="text-sm">We begin gently, allowing the body and mind to transition from "doing" to "being."</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Supported poses</h3>
                <p className="text-sm">Using bolsters, blocks, and blankets, we make each position comfortable enough to hold for several minutes.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Long holds</h3>
                <p className="text-sm">Each pose is held for 3-5 minutes, allowing the body to gradually release into the stretch.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Deep rest</h3>
                <p className="text-sm">We end in stillness, giving the body time to absorb the benefits. Some students drift into sleep.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Why Bath Residents Choose Us */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Why Bath residents travel to Frome</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              Living in <Link to="/areas/yoga-bath" className="text-gold hover:underline">Bath</Link> and looking for 
              Yin yoga? My studio in Frome is 25 minutes away, and Yin classes offer something quite different 
              from what you'll find at most gyms and studios.
            </p>
            <p>
              The slow pace of Yin requires a calm environment. My small classes (6-8 people) create the 
              space you need to truly let go — quite different from busier Bath studios. Many students say 
              the drive through the countryside becomes part of the wind-down.
            </p>
            <p>
              If you want something more active, I also offer <Link to="/areas/yoga-bath/hatha-yoga" className="text-gold hover:underline">Hatha yoga for Bath</Link> residents. 
              Many people do both — they complement each other beautifully.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Benefits of Yin Yoga</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Heart, text: "Releases deep tension" },
              { icon: Clock, text: "Improves joint mobility" },
              { icon: Users, text: "Calms nervous system" },
              { icon: Check, text: "Great for stress relief" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10 flex items-center gap-3">
                <item.icon className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="text-neutral-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions About Yin Yoga in Bath</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-neutral-700 mb-4">
            I'm Annelie, a qualified yoga teacher specialising in creating calm, nurturing spaces for practice. 
            <Link to="/about" className="text-gold hover:underline ml-1">Learn more about my approach</Link>, or 
            <Link to="/about" className="text-gold hover:underline ml-1">read what students say</Link>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Try Yin Yoga?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary" data-testid="bottom-cta">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also available for Bath residents:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/areas/yoga-bath/hatha-yoga" className="text-sm text-gold hover:underline">Hatha Yoga Bath</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-bath/private-yoga" className="text-sm text-gold hover:underline">Private Sessions Bath</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-bath" className="text-sm text-gold hover:underline">All Yoga Classes Bath</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yin-yoga" className="text-sm text-gold hover:underline">Yin Yoga (Main Page)</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default YinYogaBath;
